<template>
  <v-menu
    v-model="isOpen"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    min-width="500px"
  >
    <template #activator="{ props: menuProps }">
      <v-text-field
        v-bind="menuProps"
        v-model="displayValue"
        :label="label"
        :readonly="true"
        :clearable="true"
        :rules="rules"
        prepend-inner-icon="mdi-clock-outline"
        density="compact"
        @click:clear="clearTime"
        variant="solo-filled"
      />
    </template>

    <v-card>
      <div class="d-flex justify-space-between px-4 py-2" style="gap: 10px">
        <!-- Hora inicio -->
        <div style="flex:1">
          <div class="text-caption mb-1" v-if="range">Hora inicio</div>
          <div class="d-flex align-center">
            <v-autocomplete
              v-model="internalStartTime"
              :items="timeOptions"
              density="compact"
              hide-details
              placeholder="--:--"
              @update:modelValue="onStartChange"
              style="flex: 1"
              variant="solo-filled"
            />
            <v-select
              v-model="internalStartPeriod"
              :items="['AM','PM']"
              density="compact"
              hide-details
              style="max-width: 90px; margin-left: 6px"
              @update:modelValue="onStartPeriodChange"
              variant="solo-filled"
            />
          </div>
        </div>
        <!-- Hora fin (opcional) -->
        <div v-if="range" style="flex:1">
          <div class="text-caption mb-1">Hora fin</div>
          <div class="d-flex align-center">
            <v-autocomplete
              v-model="internalEndTime"
              :items="endOptions"
              density="compact"
              hide-details
              placeholder="--:--"
              @update:modelValue="onEndChange"
              style="flex: 1"
            />
            <v-select
              v-model="internalEndPeriod"
              :items="['AM','PM']"
              density="compact"
              hide-details
              style="max-width: 90px; margin-left: 6px"
              @update:modelValue="onEndPeriodChange"
              variant="solo-filled"
            />
          </div>
        </div>
      </div>
      <v-divider class="my-2"/>
      <div class="px-4 pb-3" v-if="range">
        <div class="text-caption mb-2">Duraciones rápidas:</div>
        <v-chip-group class="d-flex flex-wrap" :column="true">
          <v-chip
            v-for="preset in durationPresets"
            :key="preset.label"
            @click="setDurationPreset(preset)"
            :color="isDurationSelected(preset) ? 'primary' : undefined"
            class="ma-1"
            outlined
          >{{ preset.label }}</v-chip>
        </v-chip-group>
      </div>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';

const props = defineProps<{
  modelValue?: [string, string] | string | null;
  label?: string;
  rules?: ((v: any) => boolean | string)[];
  range?: boolean;
}>();

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);

const internalStartTime = ref<string | null>(null);
const internalStartPeriod = ref<'AM' | 'PM'>('AM');
const internalEndTime = ref<string | null>(null);
const internalEndPeriod = ref<'AM' | 'PM'>('AM');

const range = computed(() => props.range !== false);

const durationPresets = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1 hora', minutes: 60 },
  { label: '1.5 horas', minutes: 90 },
  { label: '2 horas', minutes: 120 },
  /*{ label: '3h', minutes: 180 },
  { label: '4h', minutes: 240 },
  { label: '8h', minutes: 480 }*/
];

// Opciones en 5 min (ej: 08:00, 08:05, ...)
function pad(n: number) { return String(n).padStart(2, '0'); }
const timeOptions = Array.from({ length: 12 * 12 }, (_, i) => {
  const h = (i / 12) | 0;
  const m = (i % 12) * 5;
  return `${pad(h === 0 ? 12 : h)}:${pad(m)}`;
});

function toMinutes(val: string, period: 'AM' | 'PM') {
  if (!val) return -1;
  const [h, m] = val.split(':').map(Number);
  let hour = h === 12 ? 0 : h;
  if (period === 'PM') hour += 12;
  return hour * 60 + m;
}
function fromMinutes(mins: number) {
  const hour = ((Math.floor(mins / 60) % 12) || 12);
  const min = mins % 60;
  const period = mins < 720 ? 'AM' : 'PM';
  return { time: `${pad(hour)}:${pad(min)}`, period };
}

const endOptions = computed(() => {
  if (!internalStartTime.value) return timeOptions;
  const startMin = toMinutes(internalStartTime.value, internalStartPeriod.value);
  const filtered: string[] = [];
  for (let period of ['AM','PM'] as const) {
    for (let opt of timeOptions) {
      const min = toMinutes(opt, period);
      if (min > startMin) filtered.push(opt);
    }
  }
  return filtered;
});

// Acepta 12h ("06:15 PM") o 24h ("18:15:00") y lo normaliza a [hh:mm, 'AM'|'PM']
function parseTo12h(str: string): [string, string] {
  if (!str) return ['12:00', 'AM'];
  const match12 = str.match(/(\d{2}):(\d{2})\s*(AM|PM)/i);
  if (match12) {
    return [`${match12[1]}:${match12[2]}`, (match12[3] || 'AM').toUpperCase()];
  }
  // Si es formato 24h
  const match24 = str.match(/(\d{2}):(\d{2})/);
  if (match24) {
    let hour = parseInt(match24[1], 10);
    const min = match24[2];
    const period = hour >= 12 ? 'PM' : 'AM';
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;
    return [`${String(hour).padStart(2, '0')}:${min}`, period];
  }
  return ['12:00', 'AM'];
}

watch(
  () => props.modelValue,
  (val) => {
    if (!val && !isOpen.value) {
      resetFields();
      return;
    }
    // Normaliza rango
    if (Array.isArray(val)) {
      const [start, end] = val;
      if (start) {
        const [sTime, sPeriod] = parseTo12h(start);
        internalStartTime.value = sTime;
        internalStartPeriod.value = sPeriod as 'AM' | 'PM';
      }
      if (end) {
        const [eTime, ePeriod] = parseTo12h(end);
        internalEndTime.value = eTime;
        internalEndPeriod.value = ePeriod as 'AM' | 'PM';
      }
    } else if (typeof val === 'string') {
      const [sTime, sPeriod] = parseTo12h(val);
      internalStartTime.value = sTime;
      internalStartPeriod.value = sPeriod as 'AM' | 'PM';
      internalEndTime.value = null;
      internalEndPeriod.value = 'AM';
    }
  },
  { immediate: true }
);


function resetFields() {
  internalStartTime.value = null;
  internalStartPeriod.value = 'AM';
  internalEndTime.value = null;
  internalEndPeriod.value = 'AM';
}

function parseTimeString(str: string): [string, string] {
  const match = str.match(/(\d{2}:\d{2})\s*(AM|PM)?/i);
  if (match) {
    return [match[1], (match[2] || 'AM').toUpperCase()];
  }
  return ['12:00', 'AM'];
}

function formatModel() {
  if (range.value) {
    if (internalStartTime.value && internalStartPeriod.value && internalEndTime.value && internalEndPeriod.value)
      return [`${internalStartTime.value} ${internalStartPeriod.value}`, `${internalEndTime.value} ${internalEndPeriod.value}`];
    return null;
  } else {
    if (internalStartTime.value && internalStartPeriod.value)
      return `${internalStartTime.value} ${internalStartPeriod.value}`;
    return '';
  }
}

// Ahora sí, nunca borra la inicial por accidente
function onStartChange(val: string) {
  if (range.value && internalEndTime.value) {
    const startMin = toMinutes(val, internalStartPeriod.value);
    const endMin = toMinutes(internalEndTime.value, internalEndPeriod.value);
    if (endMin <= startMin) {
      internalEndTime.value = null;
    }
  }
  emitModel();
}
function onStartPeriodChange(val: 'AM' | 'PM') {
  if (range.value && internalEndTime.value && internalStartTime.value) {
    const startMin = toMinutes(internalStartTime.value, val);
    const endMin = toMinutes(internalEndTime.value, internalEndPeriod.value);
    if (endMin <= startMin) {
      internalEndTime.value = null;
    }
  }
  emitModel();
}
function onEndChange() {
  emitModel();
}
function onEndPeriodChange() {
  emitModel();
}
function emitModel() {
  const out = formatModel();
  emit('update:modelValue', out);
}

// Presets
function setDurationPreset(preset: { label: string; minutes: number }) {
  if (!internalStartTime.value) return;
  const startMin = toMinutes(internalStartTime.value, internalStartPeriod.value);
  let endMin = startMin + preset.minutes;
  if (endMin >= 24 * 60) endMin = 24 * 60 - 5;
  const { time, period } = fromMinutes(endMin);
  internalEndTime.value = time;
  internalEndPeriod.value = period as 'AM' | 'PM';
  emitModel();
}
function isDurationSelected(preset: { label: string; minutes: number }) {
  if (!internalStartTime.value || !internalEndTime.value) return false;
  const start = toMinutes(internalStartTime.value, internalStartPeriod.value);
  const end = toMinutes(internalEndTime.value, internalEndPeriod.value);
  return end - start === preset.minutes;
}

const displayValue = computed(() => {
  if (!internalStartTime.value) return '';
  if (range.value && internalEndTime.value)
    return `${internalStartTime.value} ${internalStartPeriod.value} – ${internalEndTime.value} ${internalEndPeriod.value}`;
  return `${internalStartTime.value} ${internalStartPeriod.value}`;
});

function clearTime() {
  resetFields();
  emit('update:modelValue', range.value ? null : '');
}
</script>
